import { Button } from '@/components/ui/Button';
import {
  dashboardRhService,
  DashboardGenerateRequest,
} from '../services/dashboard-rh.service';
import { useMutation } from '@tanstack/react-query';
import { BarChart3, Calendar, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface DashboardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  folderName: string;
}

interface DashboardFormData {
  startMonth: string;
  startYear: string;
  endMonth: string;
  endYear: string;
}

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

export function DashboardDrawer({
  isOpen,
  onClose,
  folderUuid,
  folderName,
}: DashboardDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DashboardFormData>({
    startMonth: new Date().getMonth().toString().padStart(2, '0'),
    startYear: new Date().getFullYear().toString(),
    endMonth: new Date().getMonth().toString().padStart(2, '0'),
    endYear: new Date().getFullYear().toString(),
  });

  // Progress overlay state
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<string[]>([]);
  const progressTimerRef = useRef<number | null>(null);

  const buildSteps = (): string[] => {
    return [
      t('dashboardRh.progress.uploadFiles', 'Uploading Excel and DSN files'),
      t('dashboardRh.progress.validateFiles', 'Validating file formats'),
      t('dashboardRh.progress.parseExcel', 'Parsing Excel journal de paie'),
      t('dashboardRh.progress.parseDsn', 'Parsing DSN XML files'),
      t('dashboardRh.progress.extractIndicators', 'Extracting indicators'),
      t('dashboardRh.progress.calculateMetrics', 'Calculating metrics'),
      t('dashboardRh.progress.generateDashboard', 'Generating dashboard'),
    ];
  };

  const clearProgressTimer = () => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const startProgress = (monthsCount: number) => {
    const localSteps = buildSteps();
    setSteps(localSteps);
    setIsProgressOpen(true);
    setProgressPercent(0);
    setCurrentStepIndex(0);

    // Total simulated duration roughly proportional to months (20s to ~50s)
    const totalMs = Math.min(50000, Math.max(20000, monthsCount * 4000));
    const tickMs = 500;
    const totalTicks = Math.ceil(totalMs / tickMs);
    let tick = 0;

    clearProgressTimer();
    progressTimerRef.current = window.setInterval(() => {
      tick += 1;
      const percent = Math.min(85, Math.floor((tick / totalTicks) * 85));
      setProgressPercent(percent);

      const stepBoundary = Math.floor(85 / localSteps.length);
      const stepIndex = Math.min(
        localSteps.length - 1,
        Math.floor(percent / stepBoundary)
      );
      setCurrentStepIndex(stepIndex);

      if (tick >= totalTicks) {
        clearProgressTimer();
      }
    }, tickMs);
  };

  const generateMutation = useMutation({
    mutationFn: (request: DashboardGenerateRequest) =>
      dashboardRhService.generateDashboard(request),
    onSuccess: data => {
      // New response structure: { success, message, data: DashboardData, monthsProcessed, processingTime }
      if (!data?.data?.uuid) {
        console.error('Dashboard UUID not found in response', data);
        clearProgressTimer();
        setIsProgressOpen(false);
        return;
      }

      const dashboardUuid = data.data.uuid;
      const targetPath = `/folders/${folderUuid}/dashboard/${dashboardUuid}`;

      // Speed run the remaining steps when results arrive
      clearProgressTimer();

      // Quickly complete remaining steps
      const remainingSteps = steps.length - currentStepIndex - 1;
      if (remainingSteps > 0) {
        const speedRunInterval = setInterval(() => {
          setCurrentStepIndex(prev => {
            const next = prev + 1;
            if (next >= steps.length - 1) {
              clearInterval(speedRunInterval);
              setProgressPercent(100);
              // Close drawer and navigate immediately
              setIsProgressOpen(false);
              onClose();
              // Use setTimeout to ensure drawer is closed before navigation
              setTimeout(() => {
                navigate(targetPath);
              }, 100);
              return steps.length - 1;
            }
            return next;
          });
        }, 200);
      } else {
        // Already at last step, just finish
        setProgressPercent(100);
        // Close drawer and navigate immediately
        setIsProgressOpen(false);
        onClose();
        // Use setTimeout to ensure drawer is closed before navigation
        setTimeout(() => {
          navigate(targetPath);
        }, 100);
      }
    },
    onError: () => {
      clearProgressTimer();
      setIsProgressOpen(false);
    },
  });

  const handleInputChange = (field: keyof DashboardFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGenerate = () => {
    const request: DashboardGenerateRequest = {
      folderUuid,
      startMonth: parseInt(formData.startMonth),
      startYear: parseInt(formData.startYear),
      endMonth: parseInt(formData.endMonth),
      endYear: parseInt(formData.endYear),
    };

    // Compute months count and start simulated progress
    const monthsCount =
      (request.endYear - request.startYear) * 12 +
      (request.endMonth - request.startMonth) +
      1;
    startProgress(monthsCount);
    generateMutation.mutate(request);
  };

  const handleClose = () => {
    setFormData({
      startMonth: new Date().getMonth().toString().padStart(2, '0'),
      startYear: new Date().getFullYear().toString(),
      endMonth: new Date().getMonth().toString().padStart(2, '0'),
      endYear: new Date().getFullYear().toString(),
    });
    clearProgressTimer();
    setIsProgressOpen(false);
    setProgressPercent(0);
    setCurrentStepIndex(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Drawer */}
        <div className="fixed top-0 right-0 h-full w-xl max-w-xl bg-white shadow-xl transition-transform">
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-200 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    {t('dashboardRh.title', 'Tableau de bord')}
                  </h2>
                  <p className="text-sm text-neutral-500">
                    {t(
                      'dashboardRh.description',
                      'Générer un tableau de bord'
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Folder Info */}
                <div className="rounded-lg bg-neutral-50 p-4">
                  <p className="text-sm font-medium text-neutral-900">
                    {t('dashboardRh.folder', 'Dossier')}
                  </p>
                  <p className="text-sm text-neutral-600">{folderName}</p>
                </div>

                {/* Date Range */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-900">
                      {t('dashboardRh.dateRange', 'Période')}
                    </span>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('dashboardRh.startDate', 'Date de début')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={formData.startMonth}
                        onChange={e =>
                          handleInputChange('startMonth', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.startYear}
                        onChange={e =>
                          handleInputChange('startYear', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('dashboardRh.endDate', 'Date de fin')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={formData.endMonth}
                        onChange={e =>
                          handleInputChange('endMonth', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.endYear}
                        onChange={e =>
                          handleInputChange('endYear', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-primary-50 rounded-lg p-4">
                  <h4 className="mb-2 text-sm font-medium text-neutral-900">
                    {t('dashboardRh.summary', 'Résumé')}
                  </h4>
                  <div className="space-y-1 text-sm text-neutral-600">
                    <p>
                      <span className="font-medium">
                        {t('dashboardRh.period', 'Période')}:
                      </span>{' '}
                      {MONTHS.find(m => m.value === formData.startMonth)?.label}{' '}
                      {formData.startYear} -{' '}
                      {MONTHS.find(m => m.value === formData.endMonth)?.label}{' '}
                      {formData.endYear}
                    </p>
                    <p>
                      <span className="font-medium">
                        {t('dashboardRh.monthsCount', 'Nombre de mois')}:
                      </span>{' '}
                      {(() => {
                        const monthsCount =
                          (parseInt(formData.endYear) -
                            parseInt(formData.startYear)) *
                            12 +
                          (parseInt(formData.endMonth) -
                            parseInt(formData.startMonth)) +
                          1;
                        return monthsCount;
                      })()}
                    </p>
                  </div>
                </div>

                {/* Info */}
                <div className="rounded-lg bg-blue-50 p-4">
                  <p className="text-sm text-blue-800">
                    {t(
                      'dashboardRh.info',
                      'Le tableau de bord sera généré à partir des fichiers Excel (journaux de paie) et DSN (XML) disponibles pour cette période.'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 p-6">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={generateMutation.isPending}
                  className="flex-1"
                >
                  {generateMutation.isPending
                    ? t('common.loading', 'Chargement...')
                    : t('dashboardRh.generate', 'Générer')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overlay */}
      {isProgressOpen && (
        <div className="fixed inset-0 z-[60] flex">
          <div className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm" />
          <div className="relative z-10 m-auto w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {t(
                      'dashboardRh.progress.title',
                      'Génération du tableau de bord...'
                    )}
                  </h3>
                  <p className="text-sm text-neutral-500">
                    {t(
                      'dashboardRh.progress.subtitle',
                      'Cette opération peut prendre plusieurs secondes'
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <ul className="space-y-2">
              {steps.map((label, index) => {
                const isDone = index < currentStepIndex;
                const isActive = index === currentStepIndex;
                return (
                  <li key={index} className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                        isDone
                          ? 'bg-success-100 text-success-700'
                          : isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {isDone ? '✓' : isActive ? '•' : ''}
                    </span>
                    <span
                      className={`text-sm ${
                        isDone
                          ? 'text-neutral-600'
                          : isActive
                            ? 'text-neutral-900'
                            : 'text-neutral-500'
                      }`}
                    >
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 text-right">
              <span className="text-xs text-neutral-500">
                {t(
                  'dashboardRh.progress.note',
                  'Veuillez ne pas fermer cette fenêtre'
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
